import axios, { type InternalAxiosRequestConfig } from 'axios'
import { getNativeSessionToken } from '../auth/nativeSessionToken'
import { API_BASE_URL } from '../config/environment'
import { expireCollection } from '../stores/collectionStore'
import { handleAuthFailure } from './auth'
import { getSessionSignal } from './requestSession'
import { hasAuthenticatedUser } from './user'

const cleanups = new WeakMap<InternalAxiosRequestConfig, () => void>()

let isConfigured = false

/**
 * Configures shared axios behavior for credentials and auth failure handling.
 */
export function setupHttp(): void {
	if (isConfigured) return
	isConfigured = true

	if (API_BASE_URL) {
		axios.defaults.baseURL = API_BASE_URL
	}
	axios.interceptors.request.use(async (config) => {
		const session = getSessionSignal()
		const caller = config.signal
		const request = new AbortController()
		const abort = () => request.abort()
		session.addEventListener('abort', abort, { once: true })
		caller?.addEventListener?.('abort', abort, { once: true })
		cleanups.set(config, () => {
			session.removeEventListener('abort', abort)
			caller?.removeEventListener?.('abort', abort)
		})
		if (session.aborted || caller?.aborted) abort()
		config.signal = request.signal
		const token = await getNativeSessionToken()
		if (token) config.headers.set('Authorization', `Bearer ${token}`)
		return config
	})

	axios.interceptors.response.use(
		(response) => {
			cleanups.get(response.config)?.()
			return response
		},
		async (error: unknown) => {
			if (axios.isAxiosError(error) && error.config)
				cleanups.get(error.config)?.()
			if (
				axios.isAxiosError(error) &&
				error.response?.data?.error === 'COLLECTION_NOT_FRESH'
			)
				expireCollection()
			if (!axios.isAxiosError(error) || error.response?.status !== 401) {
				return Promise.reject(error)
			}

			const requestUrl = error.config?.url ?? ''
			const isAuthProbe = requestUrl.includes('/auth/me')
			if (isAuthProbe && !hasAuthenticatedUser()) {
				return Promise.reject(error)
			}

			await handleAuthFailure('session-expired')
			return Promise.reject(error)
		},
	)
}
